'use client';

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

type Prefs = {
  news: boolean;
  columns: boolean;
  analysis: boolean;
  email: boolean;
  // Platform-wide arming state: the owner hasn't turned the email channel on
  // yet → hide the row entirely so members never see a dead feature.
  email_available?: boolean;
};

const ROWS: { key: keyof Prefs; label: string }[] = [
  { key: 'news', label: '新聞通知' },
  { key: 'columns', label: '專欄通知' },
  { key: 'analysis', label: '分析通知' },
];

// Push category toggles — these gate delivery (a column push only reaches users
// with 專欄通知 on). Below them sits the Email channel switch: when a new
// column publishes, members get a push AND an email (the backup for devices
// that swallow pushes — OEM battery killers, PWA not installed). Disabled for
// free members: notifications are a paid perk.
export default function NotifyPrefs() {
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [paid, setPaid] = useState<boolean | null>(null); // null = loading

  useEffect(() => {
    api.notifyPrefs().then(setPrefs).catch(() => {});
    api
      .usage()
      .then((u) => setPaid(u.plan !== 'free'))
      .catch(() => setPaid(null));
  }, []);

  const locked = paid !== true; // free (or still loading) → categories are moot

  async function toggle(key: keyof Prefs) {
    if (locked || !prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); // optimistic
    try {
      await api.setNotifyPrefs(next);
    } catch {
      setPrefs(prefs); // revert on failure
    }
  }

  return (
    <>
      {ROWS.map(({ key, label }) => (
        <label
          key={key}
          className={`flex items-center justify-between py-2 text-sm ${
            locked ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <span className={locked ? 'text-zinc-400' : ''}>{label}</span>
          <input
            type="checkbox"
            checked={prefs ? prefs[key] : true}
            onChange={() => toggle(key)}
            disabled={locked || !prefs}
            className="h-5 w-5 accent-emerald-500 disabled:opacity-50"
          />
        </label>
      ))}

      {/* Email channel switch — sits apart from the push categories above.
          Rendered only once the owner arms the channel (email_available). */}
      {prefs?.email_available === true && (
      <label
        className={`mt-1 flex items-center justify-between border-t border-zinc-100 py-2 pt-3 text-sm dark:border-zinc-800 ${
          locked ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <span>
          <span className={`block ${locked ? 'text-zinc-400' : ''}`}>Email 通知</span>
          <span className="block text-[11px] leading-relaxed text-zinc-400">
            新專欄發布時同步寄通知信到你的註冊信箱(推播收不到時的備援)
          </span>
        </span>
        <input
          type="checkbox"
          checked={prefs ? prefs.email : true}
          onChange={() => toggle('email')}
          disabled={locked || !prefs}
          className="h-5 w-5 shrink-0 accent-emerald-500 disabled:opacity-50"
        />
      </label>
      )}
    </>
  );
}
